// The glimpse of the artifacts panel while it is closed (Adam, 12 Sep).
//
// The right panel no longer opens by itself: the conversation keeps the width.
// In its place, two narrow pills run down the chat's right edge from the top,
// one for each section the panel holds, so a reader can tell at a glance what
// the session has produced and how far its plan has got without giving the
// space back. Clicking either opens the panel.
//
//   Tasks      one dot per task, top to bottom, in order: done is a tick in a
//              circle, running is a filled blue dot, pending is a hollow one.
//   Agents     (Chief of Staff) one dot per delegated agent: each sub-agent,
//              each agent of a workflow from the chat's shared run poll, or
//              one dot for a workflow whose run is not known yet. Working
//              pulses blue, done is green, failed red, stopped grey. Drawn
//              first, as the Agents section is the panel's first.
//   Artifacts  a count per kind — table 6, PDF 2 — glyph over number, stacked,
//              using the same kind rule the panel's own glyphs use, so a
//              composite and a table stay distinct here too.
//
// Vertical, not a row across the corner (Adam, 12 Sep): the rail reads as the
// edge of the panel it stands in for, and a plan of eight steps takes no
// width from the conversation.
//
// A pill with nothing to say is not drawn, and with nothing at all to say the
// corner is empty: the edge handle already marks the panel's existence.

import React, { useMemo } from 'react'
import { Box, Tooltip } from '@mui/material'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import ChecklistOutlinedIcon from '@mui/icons-material/ChecklistOutlined'
import { ArtifactGlyph, artifactKind, type ArtifactKind } from './artifactGlyph.js'
import SmartToyOutlinedIcon from '@mui/icons-material/SmartToyOutlined'
import { HAIRLINE, HAIRLINE_HOVER_COLOR, PILL_RADIUS } from './surface.js'
import { delegatedStatusColor } from './SubagentCard.js'
import { workflowAgentStatus } from './AgentsSection.js'
import { useDelegatedAgents } from './DelegatedAgentsContext.js'
import type { DelegatedEntry } from '../delegatedAgents.js'
import type { ArtifactInfo, DelegatedStatus, TodoItem, WorkflowRun } from '../types.js'

/** Dots past this many collapse into a "+n" so a long plan stays one pill. */
export const PREVIEW_MAX_DOTS = 8
/** Kinds past this many collapse into "+n more" so the pill stays a pill. */
export const PREVIEW_MAX_KINDS = 4

export interface ArtifactPanelPreviewProps {
  artifacts: ArtifactInfo[]
  todos: TodoItem[]
  /** The chat's sub-agents and workflows (Chief of Staff). */
  agents?: DelegatedEntry[]
  /** The host's own panel section (AgentChat's panelSection): its pill, drawn first. */
  section?: { label: string; rail?: React.ReactNode }
  /** Opens the panel. Every pill calls it. */
  onOpen: () => void
}

/**
 * The Agents pill's dots, in list order: a sub-agent's status, each agent of
 * a known run, or the workflow's own status while its run is not known.
 */
export function agentDots(agents: DelegatedEntry[], runs: ReadonlyMap<string, WorkflowRun> | undefined): DelegatedStatus[] {
  const dots: DelegatedStatus[] = []
  for (const e of agents) {
    if (e.kind === 'subagent') { dots.push(e.info.status); continue }
    const run = e.info.runId ? runs?.get(e.info.runId) : undefined
    if (run && run.agents.length > 0) {
      for (const a of run.agents) dots.push(workflowAgentStatus(a, e.info.status))
    } else {
      dots.push(e.info.status)
    }
  }
  return dots
}

/** What to call a kind in the pill and its tooltip. Files go by extension. */
export function kindLabel(kind: ArtifactKind, ext: string, count: number): string {
  const plural = (one: string, many = `${one}s`) => (count === 1 ? one : many)
  switch (kind) {
    case 'table': return plural('table')
    case 'chart': return plural('chart')
    case 'composite': return plural('composite')
    case 'image': return plural('image')
    case 'webapp': return plural('web app')
    case 'code': return plural('script')
    case 'data': return plural('dataset')
    case 'report':
    case 'file':
      return ext ? ext.toUpperCase() : plural('file')
  }
}

interface KindCount {
  key: string
  kind: ArtifactKind
  ext: string
  count: number
  sample: ArtifactInfo
}

/** Group artifacts by what they are. Reports and files split by extension. */
export function countByKind(artifacts: ArtifactInfo[]): KindCount[] {
  const counts = new Map<string, KindCount>()
  for (const a of artifacts) {
    if (a.isDir || a.status === 'lost') continue
    const kind = artifactKind(a)
    const ext = kind === 'report' || kind === 'file' ? (a.fileName || '').split('.').pop()?.toLowerCase() || '' : ''
    const key = ext ? `${kind}.${ext}` : kind
    const row = counts.get(key)
    if (row) row.count++
    else counts.set(key, { key, kind, ext, count: 1, sample: a })
  }
  return Array.from(counts.values()).sort((x, y) => y.count - x.count || x.key.localeCompare(y.key))
}

/** Width of a rail pill, in px. Both pills share it so they read as one rail. */
export const PREVIEW_RAIL_WIDTH = 28

const pillSx = {
  display: 'inline-flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '6px',
  width: PREVIEW_RAIL_WIDTH,
  py: '8px',
  px: 0,
  m: 0,
  border: HAIRLINE,
  borderRadius: PILL_RADIUS,
  backgroundColor: 'background.paper',
  color: 'text.secondary',
  fontSize: 12,
  fontWeight: 500,
  lineHeight: 1,
  cursor: 'pointer',
  whiteSpace: 'nowrap',
  transition: 'border-color 160ms ease, color 160ms ease, box-shadow 160ms ease',
  '&:hover': { borderColor: HAIRLINE_HOVER_COLOR, color: 'text.primary' },
  '&:focus-visible': { outline: '2px solid', outlineColor: 'info.main', outlineOffset: 1 },
} as const

function TaskDot({ status }: { status: TodoItem['status'] }) {
  if (status === 'completed') {
    return <CheckCircleIcon data-status="completed" sx={{ fontSize: 13, color: '#16a34a' }} />
  }
  return (
    <Box
      component="span"
      data-status={status}
      sx={{
        width: 9,
        height: 9,
        borderRadius: '50%',
        boxSizing: 'border-box',
        ...(status === 'in_progress'
          ? { backgroundColor: 'info.main' }
          : { border: '1.5px solid', borderColor: 'rgba(38,55,87,0.35)' }),
      }}
    />
  )
}

function AgentDot({ status }: { status: DelegatedStatus }) {
  return (
    <Box
      component="span"
      data-status={status}
      sx={{
        width: 9,
        height: 9,
        borderRadius: '50%',
        backgroundColor: delegatedStatusColor(status),
        ...(status === 'running' && {
          animation: 'agentRailPulse 1.2s ease-in-out infinite',
          '@keyframes agentRailPulse': { '0%, 100%': { opacity: 1 }, '50%': { opacity: 0.35 } },
        }),
      }}
    />
  )
}

export default function ArtifactPanelPreview({ artifacts, todos, agents, section, onOpen }: ArtifactPanelPreviewProps) {
  const runs = useDelegatedAgents()?.runs
  const kinds = useMemo(() => countByKind(artifacts), [artifacts])
  const dots = useMemo(() => agentDots(agents ?? [], runs), [agents, runs])
  const done = todos.filter(t => t.status === 'completed').length
  const showTasks = todos.length > 0
  const showArtifacts = kinds.length > 0
  const showAgents = dots.length > 0
  if (!section && !showTasks && !showArtifacts && !showAgents) return null

  const shownAgentDots = dots.slice(0, PREVIEW_MAX_DOTS)
  const moreAgentDots = dots.length - shownAgentDots.length
  const working = dots.filter(d => d === 'running').length
  const agentWords = `${dots.length} ${dots.length === 1 ? 'agent' : 'agents'}${working ? `, ${working} working` : ''}`

  const shownDots = todos.slice(0, PREVIEW_MAX_DOTS)
  const moreDots = todos.length - shownDots.length
  const shownKinds = kinds.slice(0, PREVIEW_MAX_KINDS)
  const moreKinds = kinds.length - shownKinds.length
  const artifactWords = kinds.map(k => `${k.count} ${kindLabel(k.kind, k.ext, k.count)}`).join(', ')

  return (
    <Box
      data-testid="artifact-panel-preview"
      data-orientation="vertical"
      sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '6px' }}
    >
      {section && (
        <Tooltip title={`${section.label}. Open the panel.`} placement="bottom">
          <Box
            component="button"
            type="button"
            data-testid="artifact-panel-preview-section"
            aria-label={`${section.label}. Open the panel`}
            onClick={onOpen}
            sx={pillSx}
          >
            {section.rail ?? <ChecklistOutlinedIcon aria-hidden sx={{ fontSize: 15, color: 'text.secondary' }} />}
          </Box>
        </Tooltip>
      )}
      {showAgents && (
        <Tooltip title={`${agentWords}. Open the panel for the list.`} placement="bottom">
          <Box
            component="button"
            type="button"
            data-testid="agent-panel-preview-agents"
            aria-label={`Agents: ${agentWords}. Open artifacts panel`}
            onClick={onOpen}
            sx={pillSx}
          >
            <SmartToyOutlinedIcon aria-hidden sx={{ fontSize: 15, color: 'text.secondary' }} />
            <Box component="span" sx={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'center', gap: '5px' }}>
              {shownAgentDots.map((d, i) => <AgentDot key={i} status={d} />)}
            </Box>
            {moreAgentDots > 0 && <Box component="span" sx={{ fontSize: 10 }}>+{moreAgentDots}</Box>}
          </Box>
        </Tooltip>
      )}
      {showTasks && (
        <Tooltip title={`${done} of ${todos.length} tasks done. Open the panel for the list.`} placement="bottom">
          <Box
            component="button"
            type="button"
            data-testid="artifact-panel-preview-tasks"
            aria-label={`Tasks: ${done} of ${todos.length} done. Open artifacts panel`}
            onClick={onOpen}
            sx={pillSx}
          >
            {/* The word "Tasks" does not fit a rail; the checklist glyph stands for it,
                and the tooltip and accessible name still say the word. */}
            <ChecklistOutlinedIcon aria-hidden sx={{ fontSize: 15, color: 'text.secondary' }} />
            <Box component="span" sx={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'center', gap: '5px' }}>
              {shownDots.map((t, i) => <TaskDot key={i} status={t.status} />)}
            </Box>
            {moreDots > 0 && <Box component="span" sx={{ fontSize: 10 }}>+{moreDots}</Box>}
          </Box>
        </Tooltip>
      )}
      {showArtifacts && (
        <Tooltip title={`${artifactWords}. Open the panel for the files.`} placement="bottom">
          <Box
            component="button"
            type="button"
            data-testid="artifact-panel-preview-artifacts"
            aria-label={`Artifacts: ${artifactWords}. Open artifacts panel`}
            onClick={onOpen}
            sx={pillSx}
          >
            {shownKinds.map(k => (
              <Box key={k.key} component="span" data-testid={`artifact-panel-preview-kind-${k.key}`} sx={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'center', gap: '1px' }}>
                <ArtifactGlyph artifact={k.sample} size={13} />
                <Box component="span" sx={{ fontSize: 11, fontVariantNumeric: 'tabular-nums' }}>{k.count}</Box>
              </Box>
            ))}
            {moreKinds > 0 && <Box component="span" sx={{ fontSize: 10 }}>+{moreKinds}</Box>}
          </Box>
        </Tooltip>
      )}
    </Box>
  )
}
